import React, { useState, useEffect } from 'react';
import { reviewService } from '../../services/ReviewService';
import { ProductReview } from '../../types';
import { 
    MessageSquare, CheckCircle, XCircle, Trash2, 
    Star, Clock, User, ImageIcon, Loader2, Link as LinkIcon
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';

const AdminReviewsPage: React.FC = () => {
    const [reviews, setReviews] = useState<ProductReview[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        loadReviews();
    }, []);

    const loadReviews = async () => {
        setIsLoading(true);
        try {
            const data = await reviewService.getAllReviews();
            setReviews(data);
        } catch (error) {
            console.error('Error loading reviews:', error);
            showToast('Error al cargar las reseñas', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected') => {
        try {
            await reviewService.updateReviewStatus(id, status);
            showToast(`Reseña ${status === 'approved' ? 'aprobada' : 'rechazada'}`, 'success');
            loadReviews();
        } catch (error) {
            console.error('Error updating status:', error);
            showToast('Error al actualizar el estado', 'error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('¿Estás seguro de eliminar esta reseña permanentemente?')) return;
        try {
            await reviewService.deleteReview(id);
            showToast('Reseña eliminada', 'success');
            loadReviews();
        } catch (error) {
            console.error('Error deleting review:', error);
            showToast('Error al eliminar la reseña', 'error');
        }
    };

    const handleGenerateLink = async () => {
        try {
            const token = await reviewService.generateToken();
            const reviewUrl = `${window.location.origin}/?review=true&token=${token}`;
            await navigator.clipboard.writeText(reviewUrl);
            showToast('Link de reseña copiado al portapapeles', 'success');
        } catch (error) {
            console.error('Error generating link:', error);
            showToast('Error al generar el link', 'error');
        }
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('es-SV', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-20">
            {/* Header / Actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-zinc-900/50 p-6 border border-zinc-800 rounded-sm">
                <div>
                    <h2 className="text-xl font-display font-black uppercase italic text-white flex items-center gap-3">
                        <MessageSquare className="text-zinc-500" /> Gestión de Reseñas
                    </h2>
                    <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest mt-1">
                        Control de calidad y social proof
                    </p>
                </div>
                <button
                    onClick={handleGenerateLink}
                    className="bg-white text-black px-6 py-3 rounded-sm font-bold uppercase text-xs tracking-widest hover:bg-zinc-200 transition-all active:scale-95 flex items-center gap-2 shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                >
                    <LinkIcon size={16} /> Generar Link de Review
                </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-sm">
                    <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-1">Pendientes</p>
                    <p className="text-2xl font-display font-black text-blue-400">
                        {reviews.filter(r => r.status === 'pending').length}
                    </p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-sm">
                    <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-1">Aprobadas</p>
                    <p className="text-2xl font-display font-black text-green-400">
                        {reviews.filter(r => r.status === 'approved').length}
                    </p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-sm">
                    <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-1">Promedio</p>
                    <p className="text-2xl font-display font-black text-white">
                        {(reviews.reduce((acc, r) => acc + r.rating, 0) / (reviews.length || 1)).toFixed(1)} <Star size={16} className="inline mb-1 fill-white" />
                    </p>
                </div>
            </div>

            {/* Reviews List */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-zinc-950 border-b border-zinc-800">
                                <th className="px-6 py-4 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest">Cliente / Fecha</th>
                                <th className="px-6 py-4 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest">Reseña</th>
                                <th className="px-6 py-4 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest text-center">Calificación</th>
                                <th className="px-6 py-4 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest text-center">Estado</th>
                                <th className="px-6 py-4 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800/50">
                            {reviews.map((review) => (
                                <tr key={review.id} className="hover:bg-white/[0.02] transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-white uppercase">{review.customer_name}</span>
                                            <span className="text-[10px] font-mono text-zinc-500 flex items-center gap-1 mt-1">
                                                <Clock size={10} /> {formatDate(review.created_at)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 max-w-md">
                                        <div className="flex flex-col gap-2">
                                            <p className="text-sm text-zinc-300 line-clamp-2 italic">"{review.comment || 'Sin comentario'}"</p>
                                            {review.media && review.media.length > 0 && (
                                                <div className="flex gap-1">
                                                    {review.media.map((m, i) => (
                                                        <div key={i} className="relative w-8 h-8 rounded-sm overflow-hidden border border-zinc-800">
                                                            <img src={m.media_url} alt="Review" className="w-full h-full object-cover" />
                                                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                                                <ImageIcon size={10} className="text-white" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center gap-0.5">
                                            {[...Array(5)].map((_, i) => (
                                                <Star 
                                                    key={i} 
                                                    size={12} 
                                                    className={i < review.rating ? "fill-white text-white" : "text-zinc-700"} 
                                                />
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border ${
                                            review.status === 'approved' 
                                                ? 'bg-green-500/10 text-green-400 border-green-500/20' 
                                                : review.status === 'rejected'
                                                ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                                : 'bg-blue-500/10 text-blue-400 border-blue-500/20 animate-pulse'
                                        }`}>
                                            {review.status === 'approved' ? 'Aprobada' : review.status === 'rejected' ? 'Rechazada' : 'Pendiente'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            {review.status !== 'approved' && (
                                                <button 
                                                    onClick={() => handleUpdateStatus(review.id, 'approved')}
                                                    className="p-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-sm transition-all"
                                                    title="Aprobar"
                                                >
                                                    <CheckCircle size={16} />
                                                </button>
                                            )}
                                            {review.status !== 'rejected' && (
                                                <button 
                                                    onClick={() => handleUpdateStatus(review.id, 'rejected')}
                                                    className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-sm transition-all"
                                                    title="Rechazar"
                                                >
                                                    <XCircle size={16} />
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => handleDelete(review.id)}
                                                className="p-2 bg-zinc-800 text-zinc-500 hover:bg-zinc-700 hover:text-white rounded-sm transition-all"
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {reviews.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-20 text-center text-zinc-600 font-mono text-xs uppercase italic">
                                        No se encontraron reseñas.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminReviewsPage;
